import { useState } from "react";
import { Download, Eye, FileText, Loader2, Paperclip } from "lucide-react";

import {
  KaiConversationAttachmentPreviewDialog,
  type KaiConversationAttachmentRef,
} from "@/components/common/KaiConversationAttachmentPreviewDialog";
import { isInlinePreviewableFilename } from "@/lib/kaiAttachments";
import { cn } from "@/lib/utils";
import { kaiApi } from "@/services/kaiApi";

import type { KaiRateSourceAttachmentSummary } from "./kaiToolDownloads";

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Download button for a single Kai-upload rate-card source document. */
function RateSourceDownloadButton({
  att,
}: {
  att: KaiRateSourceAttachmentSummary;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    try {
      const objectUrl = await kaiApi.getAttachmentRawUrl(
        att.conversationId,
        att.attachmentId,
      );
      try {
        const response = await fetch(objectUrl);
        const blob = await response.blob();
        triggerBrowserDownload(blob, att.filename);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (err) {
      console.warn("Failed to download rate card source file:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40",
        downloading && "opacity-60",
      )}
    >
      {downloading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      Download
    </button>
  );
}

/**
 * Compact card rendering the source document(s) behind Kai-created rate cards.
 * These files live on a Kai conversation upload (not an ingested batch file), so
 * the chat surfaces them even when `batch_attachments` is empty. One row per
 * file with its carrier/contract subtitle and a Download button.
 */
export function KaiRateSourceCard({
  attachments,
  embedded = false,
}: {
  attachments: KaiRateSourceAttachmentSummary[];
  /** When nested inside another Kai artifact, skip the extra glass panel. */
  embedded?: boolean;
}) {
  const [preview, setPreview] = useState<KaiConversationAttachmentRef | null>(
    null,
  );

  if (attachments.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        embedded
          ? "border-t border-border/40 pt-3"
          : "glass-panel my-1 rounded-xl p-3",
      )}
    >
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Paperclip className="size-4 shrink-0" />
        {attachments.length === 1
          ? "Rate card source file"
          : `${attachments.length} rate card source files`}
      </span>

      <div className="flex flex-col divide-y divide-border/40">
        {attachments.map((att) => {
          const subtitle = [att.carrier, att.contractRef]
            .filter(Boolean)
            .join(" \u00b7 ");
          const previewable = isInlinePreviewableFilename(att.filename);
          return (
            <div
              key={att.key}
              className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{att.filename}</span>
                </span>
                {subtitle && (
                  <span className="truncate text-[11px] text-muted-foreground">
                    {subtitle}
                  </span>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {previewable && (
                  <button
                    type="button"
                    onClick={() =>
                      setPreview({
                        conversationId: att.conversationId,
                        attachmentId: att.attachmentId,
                        filename: att.filename,
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40"
                  >
                    <Eye className="size-3.5" />
                    Preview
                  </button>
                )}
                <RateSourceDownloadButton att={att} />
              </div>
            </div>
          );
        })}
      </div>

      <KaiConversationAttachmentPreviewDialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        attachment={preview}
        previewDescription="Rate card source file preview"
      />
    </div>
  );
}
